import { SessionPort, SessionTokens } from '../ports';

export class BrowserSessionAdapter implements SessionPort {
  private navigator?: (path: string) => void;
  private sessionClearedListeners: Set<() => void> = new Set();

  getTenantId(): string | null {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        return parsed.tenantId || null;
      }
    } catch {
      // Ignore parse failure
    }
    return null;
  }

  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  getSuperuserToken(): string | null {
    try {
      const session = localStorage.getItem('superuserSession');
      if (session) {
        const parsed = JSON.parse(session);
        return parsed.accessToken || null;
      }
    } catch {
      // Ignore parse failure
    }
    return null;
  }

  saveTenantTokens(tokens: SessionTokens): void {
    if (tokens.accessToken) {
      localStorage.setItem('authToken', tokens.accessToken);
    }
    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
  }

  saveSuperuserTokens(tokens: SessionTokens): void {
    try {
      const existing = localStorage.getItem('superuserSession');
      const parsed = existing ? JSON.parse(existing) : {};
      parsed.accessToken = tokens.accessToken;
      if (tokens.refreshToken) {
        parsed.refreshToken = tokens.refreshToken;
      }
      localStorage.setItem('superuserSession', JSON.stringify(parsed));
    } catch {
      localStorage.setItem(
        'superuserSession',
        JSON.stringify({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken })
      );
    }
  }

  clearSession(isPlatform: boolean): void {
    const currentPath = this.getCurrentPath();

    if (isPlatform) {
      localStorage.removeItem('superuserSession');
      this.notifySessionCleared();
      if (!currentPath.includes('/platform/auth/login')) {
        this.navigate('/platform/auth/login');
      }
    } else {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      this.notifySessionCleared();

      if (!currentPath.includes('/login') && !currentPath.includes('/session-expired')) {
        localStorage.setItem('redirectAfterLogin', currentPath);
      }
      this.navigate('/auth/login');
    }
  }

  setNavigator(navigator: (path: string) => void): void {
    this.navigator = navigator;
  }

  navigate(path: string): void {
    if (this.navigator) {
      this.navigator(path);
    } else if (typeof window !== 'undefined' && window.location) {
      window.location.replace(path);
    }
  }

  getCurrentPath(): string {
    return typeof window !== 'undefined' && window.location ? window.location.pathname : '';
  }

  onSessionCleared(listener: () => void): () => void {
    this.sessionClearedListeners.add(listener);
    return () => this.sessionClearedListeners.delete(listener);
  }

  private notifySessionCleared(): void {
    this.sessionClearedListeners.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.error('Error in session cleared listener:', err);
      }
    });
  }
}

/**
 * In-memory session adapter for tests.
 */
export class InMemorySessionAdapter implements SessionPort {
  tenantId: string | null = null;
  authToken: string | null = null;
  refreshToken: string | null = null;
  superuserToken: string | null = null;
  currentPath: string = '/';
  lastNavigatedPath: string | null = null;
  private navigator?: (path: string) => void;
  private sessionClearedListeners: Set<() => void> = new Set();

  getTenantId(): string | null {
    return this.tenantId;
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  getSuperuserToken(): string | null {
    return this.superuserToken;
  }

  saveTenantTokens(tokens: SessionTokens): void {
    this.authToken = tokens.accessToken;
    if (tokens.refreshToken) {
      this.refreshToken = tokens.refreshToken;
    }
  }

  saveSuperuserTokens(tokens: SessionTokens): void {
    this.superuserToken = tokens.accessToken;
  }

  clearSession(isPlatform: boolean): void {
    if (isPlatform) {
      this.superuserToken = null;
      this.navigate('/platform/auth/login');
    } else {
      this.authToken = null;
      this.refreshToken = null;
      this.tenantId = null;
      this.navigate('/auth/login');
    }
    this.sessionClearedListeners.forEach((l) => l());
  }

  setNavigator(navigator: (path: string) => void): void {
    this.navigator = navigator;
  }

  navigate(path: string): void {
    this.lastNavigatedPath = path;
    if (this.navigator) {
      this.navigator(path);
    }
  }

  getCurrentPath(): string {
    return this.currentPath;
  }

  onSessionCleared(listener: () => void): () => void {
    this.sessionClearedListeners.add(listener);
    return () => this.sessionClearedListeners.delete(listener);
  }
}
